import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  GenerationResponse,
  produceSongs,
} from '../../services/generationsApi';
import { applyProduceErrorModalFromApiError } from '../../utils/applyProduceErrorModalFromApiError';
import { analytics } from '../../utils/analytics';

interface UseGenerationProduceParams {
  activeMusicGeneration?: GenerationResponse;
  isRegeneratingMusic: boolean;
  setIsRegeneratingMusic: (value: boolean) => void;
  editedLyrics: string | null;
  songsAreReady: boolean;
  isMusicGenerationSession: boolean;
  hasLyrics: boolean;
  hasSavedSong1: React.MutableRefObject<boolean>;
  hasSavedSong2: React.MutableRefObject<boolean>;
}

interface UseGenerationProduceResult {
  handleProduceSongs: () => Promise<void>;
  countdownSeconds: number | null;
  showExtendedMessage: boolean;
  showProduceErrorModal: boolean;
  isContentModerationError: boolean;
  moderationReason: string;
  setShowProduceErrorModal: (value: boolean) => void;
  setIsContentModerationError: (value: boolean) => void;
  setModerationReason: (value: string) => void;
  hasClickedProduce: boolean;
}

export const useGenerationProduce = ({
  activeMusicGeneration,
  isRegeneratingMusic,
  setIsRegeneratingMusic,
  editedLyrics,
  songsAreReady,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isMusicGenerationSession: _isMusicGenerationSession,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hasLyrics: _hasLyrics,
  hasSavedSong1,
  hasSavedSong2,
}: UseGenerationProduceParams): UseGenerationProduceResult => {
  const queryClient = useQueryClient();
  // const isStationMode = useStationMode(); // No longer needed since auto-produce is disabled
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const [showExtendedMessage, setShowExtendedMessage] = useState(false);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasAutoProducedRef = useRef(false);
  const [showProduceErrorModal, setShowProduceErrorModal] = useState(false);
  const [isContentModerationError, setIsContentModerationError] =
    useState(false);
  const [moderationReason, setModerationReason] = useState('');
  const [hasClickedProduce, setHasClickedProduce] = useState(false);

  const handleProduceSongs = useCallback(async () => {
    if (
      !activeMusicGeneration?.taskId ||
      isRegeneratingMusic ||
      hasClickedProduce
    )
      return;

    setHasClickedProduce(true);

    analytics.logEvent('music_generation_produce', {
      lyrics_edited: !!editedLyrics,
    });

    setIsRegeneratingMusic(true);
    console.log('Starting song production:', activeMusicGeneration.taskId);

    setCountdownSeconds(120);
    setShowExtendedMessage(false);

    hasSavedSong1.current = false;
    hasSavedSong2.current = false;

    queryClient.invalidateQueries({
      queryKey: ['generationStatus', activeMusicGeneration.taskId],
    });

    try {
      const lyricsToSend =
        editedLyrics || activeMusicGeneration.lyrics || undefined;

      const response = await produceSongs(
        activeMusicGeneration.taskId,
        lyricsToSend,
      );

      console.log('Production started, new taskId:', response.taskId);

      queryClient.invalidateQueries({
        queryKey: ['generationStatus', activeMusicGeneration.taskId],
      });
    } catch (error: unknown) {
      console.error('Failed to produce songs:', error);
      setIsRegeneratingMusic(false);
      setCountdownSeconds(null);
      setShowExtendedMessage(false);
      hasAutoProducedRef.current = false;

      applyProduceErrorModalFromApiError(error, {
        setShowProduceErrorModal,
        setIsContentModerationError,
        setModerationReason,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeMusicGeneration,
    isRegeneratingMusic,
    hasClickedProduce,
    editedLyrics,
    setIsRegeneratingMusic,
    // hasSavedSong1 and hasSavedSong2 are refs (stable references), don't need to be in deps
  ]);

  // useEffect(() => {
  //   if (
  //     isStationMode &&
  //     isMusicGenerationSession &&
  //     hasLyrics &&
  //     !hasAutoProducedRef.current &&
  //     !isRegeneratingMusic &&
  //     !songsAreReady &&
  //     activeMusicGeneration?.taskId
  //   ) {
  //     hasAutoProducedRef.current = true;
  //     void handleProduceSongs();
  //   }
  // }, [
  //   isStationMode,
  //   isMusicGenerationSession,
  //   hasLyrics,
  //   isRegeneratingMusic,
  //   songsAreReady,
  //   activeMusicGeneration?.taskId,
  //   handleProduceSongs,
  // ]);

  useEffect(() => {
    if (countdownSeconds === null || countdownSeconds <= 0) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }

      if (countdownSeconds === 0 && isRegeneratingMusic && !songsAreReady) {
        setShowExtendedMessage(true);
      }
      return;
    }

    countdownIntervalRef.current = setInterval(() => {
      setCountdownSeconds((prev) => {
        if (prev === null || prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [countdownSeconds, isRegeneratingMusic, songsAreReady]);

  useEffect(() => {
    if (songsAreReady) {
      setCountdownSeconds(null);
      setShowExtendedMessage(false);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
  }, [songsAreReady]);

  useEffect(() => {
    hasAutoProducedRef.current = false;
    setHasClickedProduce(false);
  }, [activeMusicGeneration?.taskId]);

  return {
    handleProduceSongs,
    countdownSeconds,
    showExtendedMessage,
    showProduceErrorModal,
    isContentModerationError,
    moderationReason,
    setShowProduceErrorModal,
    setIsContentModerationError,
    setModerationReason,
    hasClickedProduce,
  };
};
